import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import type { LiveSession, QuizQuestion } from '../types';
import { joinSessionAsParticipant, listenLiveSession, listenParticipants, resolveSessionIdByPin, submitAnswer } from '../services/sessionService';
import { normalizeJoinCode } from '../../quizLive/normalizeJoinCode';
import { validateLiveSessionByPin, validateLiveSessionBySessionId } from '../../quizLive/validateLiveSession';
import { appPath } from '../../utils/appRouting';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

function normalizeCode(code: string | undefined): string {
  return normalizeJoinCode(String(code || '')).slice(0, 6);
}

function msLeft(endsAt: number | null | undefined): number {
  if (!endsAt) return 0;
  return Math.max(0, endsAt - Date.now());
}

function toMillisMaybe(v: any): number | null {
  if (!v) return null;
  if (typeof v === 'number' && Number.isFinite(v)) {
    // If someone stored seconds instead of ms, convert.
    if (v > 0 && v < 1_000_000_000_000) return Math.round(v * 1000);
    return Math.round(v);
  }
  if (typeof v === 'string') {
    const n = Number(v);
    if (Number.isFinite(n)) return toMillisMaybe(n);
  }
  if (typeof v?.toMillis === 'function') {
    const n = Number(v.toMillis());
    return Number.isFinite(n) ? n : null;
  }
  // Firestore Timestamp-like (seconds/nanoseconds)
  if (Number.isFinite(v?.seconds)) {
    return Math.round(Number(v.seconds) * 1000);
  }
  return null;
}

function computeEndsAtMs(session: any): number | null {
  const cur = session?.current;
  if (!cur) return null;

  // 1) Legacy absolute endsAt
  const legacy = toMillisMaybe(cur.endsAt);
  if (legacy) return legacy;

  // 2) Server-anchored startedAt + durationSec
  const startedAtMs = toMillisMaybe(cur.startedAt);
  const durationSec = Number(cur.durationSec);
  if (startedAtMs && Number.isFinite(durationSec) && durationSec > 0) {
    return startedAtMs + Math.round(durationSec * 1000);
  }

  return null;
}

function computeStartedAtMs(session: any): number | null {
  const cur = session?.current;
  if (!cur) return null;
  return toMillisMaybe(cur.startedAt);
}

function computeDurationMs(session: any): number | null {
  const cur = session?.current;
  if (!cur) return null;
  const durationSec = Number(cur.durationSec);
  if (Number.isFinite(durationSec) && durationSec > 0) return Math.round(durationSec * 1000);

  // Fallback: if we only have endsAt and startedAt, compute from them.
  const startedAtMs = toMillisMaybe(cur.startedAt);
  const endsAtMs = toMillisMaybe(cur.endsAt);
  if (startedAtMs && endsAtMs && endsAtMs > startedAtMs) return endsAtMs - startedAtMs;

  return null;
}

function speak(text: string) {
  const t = String(text || '').trim();
  if (!t) return;
  if (typeof window === 'undefined') return;
  const synth = (window as any).speechSynthesis as SpeechSynthesis | undefined;
  if (!synth) return;
  try {
    synth.cancel();
    const u = new SpeechSynthesisUtterance(t);
    u.lang = 'it-IT';
    synth.speak(u);
  } catch {
    // ignore
  }
}

export default function StudentLiveView() {
  const navigate = useNavigate();
  const params = useParams();
  const query = useQuery();

  const directSessionId = useMemo(() => String((params as any).sessionId || '').trim(), [params]);
  const code = useMemo(() => normalizeCode((params as any).pin), [params]);
  const nicknameFromUrl = String(query.get('n') || '').trim().slice(0, 20);

  const [nickname, setNickname] = useState(() => {
    const fromUrl = nicknameFromUrl;
    if (fromUrl) return fromUrl;
    try {
      return String(window.localStorage.getItem('live_quiz_nickname') || '');
    } catch {
      return '';
    }
  });

  const [status, setStatus] = useState<'loading' | 'join' | 'ready' | 'error'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('Sessione non trovata.');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [participantId, setParticipantId] = useState<string | null>(null);
  const [session, setSession] = useState<LiveSession | null>(null);

  const [lastAnsweredQuestionId, setLastAnsweredQuestionId] = useState<string | null>(null);
  const [lastOptionId, setLastOptionId] = useState<string | null>(null);
  const [pendingOptionId, setPendingOptionId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const tickerRef = useRef<number | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    // If we already have a sessionId in the route, validate it server-side.
    if (directSessionId) {
      let cancelled = false;
      setStatus('loading');
      (async () => {
        const validated = await validateLiveSessionBySessionId(directSessionId, { requireStarted: false, timeoutMs: 8000 });
        if (cancelled) return;
        if (validated.ok && validated.sessionId) {
          setSessionId(validated.sessionId);
          setStatus('join');
          return;
        }
        if (!validated.ok) setErrorMessage(validated.message);
        setSessionId(null);
        setStatus('error');
      })().catch(() => {
        if (cancelled) return;
        setErrorMessage('Servizio momentaneamente non disponibile. Riprova tra poco.');
        setSessionId(null);
        setStatus('error');
      });

      return () => {
        cancelled = true;
      };
    }

    if (!/^[A-Z0-9]{6}$/.test(code)) {
      setErrorMessage('Codice non valido. Deve essere di 6 caratteri (A-Z, 0-9).');
      setStatus('error');
      return;
    }

    let cancelled = false;
    setStatus('loading');

    (async () => {
      try {
        const validated = await validateLiveSessionByPin(code, { requireStarted: false, timeoutMs: 8000 });
        if (cancelled) return;
        if (validated.ok && validated.sessionId) {
          setSessionId(validated.sessionId);
          setStatus('join');
          return;
        }

        if (!validated.ok) {
          setErrorMessage(validated.message);
        }

        // Dev-only fallback to direct Firestore resolve, to keep local testing usable.
        if (process.env.NODE_ENV !== 'production') {
          const sid = await resolveSessionIdByPin(code);
          if (cancelled) return;
          if (sid) {
            setSessionId(sid);
            setStatus('join');
            return;
          }
        }

        setStatus('error');
        setSessionId(null);
      } catch {
        if (cancelled) return;
        setErrorMessage('Servizio momentaneamente non disponibile. Riprova tra poco.');
        setStatus('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, directSessionId]);

  useEffect(() => {
    if (!sessionId) return;
    return listenLiveSession(sessionId, setSession);
  }, [sessionId]);

  const [finalParticipants, setFinalParticipants] = useState<any[]>([]);
  useEffect(() => {
    if (!sessionId) return;
    // Subscribe to participants to render the final leaderboard when the session ends.
    return listenParticipants(sessionId, setFinalParticipants as any);
  }, [sessionId]);

  useEffect(() => {
    if (tickerRef.current) window.clearInterval(tickerRef.current);
    tickerRef.current = window.setInterval(() => setTick((t) => t + 1), 500);
    return () => {
      if (tickerRef.current) window.clearInterval(tickerRef.current);
    };
  }, []);

  const currentQuestion: QuizQuestion | null = useMemo(() => {
    const q = (session as any)?.current?.question;
    if (!q) return null;
    return q as QuizQuestion;
  }, [session?.current?.question]);

  useEffect(() => {
    // Reset pending selection when the question changes.
    if (!currentQuestion?.id) {
      setPendingOptionId(null);
      return;
    }
    if (lastAnsweredQuestionId === currentQuestion.id && lastOptionId) {
      setPendingOptionId(lastOptionId);
    } else {
      setPendingOptionId(null);
    }
  }, [currentQuestion?.id, lastAnsweredQuestionId, lastOptionId]);

  const currentEndsAtMs = useMemo(() => computeEndsAtMs(session as any), [session]);
  const currentStartedAtMs = useMemo(() => computeStartedAtMs(session as any), [session]);
  const currentDurationMs = useMemo(() => computeDurationMs(session as any), [session]);
  const timeLeftSec = Math.ceil(msLeft(currentEndsAtMs) / 1000);
  const timeLeftMs = msLeft(currentEndsAtMs);

  const timerPct = useMemo(() => {
    if (!currentEndsAtMs) return 0;
    const duration = currentDurationMs;
    if (!duration || duration <= 0) return 0;
    const pct = (timeLeftMs / duration) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [currentEndsAtMs, currentDurationMs, timeLeftMs]);

  const isInclusive = Boolean(session?.inclusive);
  const disableAnswering =
    !sessionId ||
    !participantId ||
    !currentQuestion ||
    session?.status !== 'question' ||
    msLeft(currentEndsAtMs) <= 0 ||
    (session?.current?.mode === 'collective');

  const hasAnsweredThis = Boolean(currentQuestion && lastAnsweredQuestionId === currentQuestion.id);
  const canConfirm = Boolean(pendingOptionId && !disableAnswering && !submitting && !hasAnsweredThis);

  const onJoin = async () => {
    if (!sessionId) return;

    setSubmitting(true);
    try {
      const n = nickname.trim().slice(0, 20);
      try {
        window.localStorage.setItem('live_quiz_nickname', n);
      } catch {
        // ignore
      }

      const pid = await joinSessionAsParticipant({ sessionId, nickname: n || undefined, isAnonymous: !n });
      setParticipantId(pid);
      setStatus('ready');
    } catch (e: any) {
      const msg = String(e?.message || '');
      if (msg === 'ANON_AUTH_DISABLED' || String(e?.code || '') === 'auth/operation-not-allowed') {
        setErrorMessage(
          'Accesso studente non disponibile: Firebase Auth “Anonimo” non è abilitato. Attivalo in Firebase Console → Authentication → Sign-in method → Anonymous.'
        );
      } else {
        setErrorMessage('Impossibile entrare nella sessione. Riprova tra qualche secondo.');
      }
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const sendAnswer = async (optionId: string) => {
    if (!sessionId || !participantId || !currentQuestion) return;
    if (disableAnswering) return;

    setSubmitting(true);
    try {
      const answeredAt = Date.now();
      await submitAnswer({ sessionId, questionId: currentQuestion.id, participantId, optionId, answeredAt });
      setLastAnsweredQuestionId(currentQuestion.id);
      setLastOptionId(optionId);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPending = async () => {
    if (!pendingOptionId) return;
    await sendAnswer(pendingOptionId);
  };

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-4">
          <div className="text-lg font-semibold">Impossibile entrare</div>
          <div className="mt-1 text-sm text-muted-foreground">{errorMessage}</div>
          <button
            type="button"
            className="mt-4 w-full px-4 py-2 rounded-xl bg-muted text-foreground"
            onClick={() => navigate(appPath('/live/join'))}
          >
            Torna al join
          </button>
        </div>
      </div>
    );
  }

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="text-sm text-muted-foreground">Connessione alla sessione…</div>
      </div>
    );
  }

  if (status === 'join') {
    return (
      <div className="min-h-screen bg-background text-foreground flex items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="text-xs text-muted-foreground">Quiz Live</div>
            <div className="text-lg font-semibold">Codice {code || '—'}</div>
          </div>
          <div className="p-4 space-y-3">
            <label className="block">
              <div className="text-xs text-muted-foreground mb-1">Nickname (opzionale)</div>
              <input
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                className="w-full px-3 py-2 rounded-xl bg-background border border-border text-foreground text-base"
                placeholder="Es: Sara"
              />
            </label>
            <button
              type="button"
              onClick={onJoin}
              disabled={submitting}
              className={
                'w-full px-4 py-2 rounded-xl text-sm ' +
                (submitting ? 'bg-muted text-muted-foreground' : 'bg-primary text-primary-foreground')
              }
            >
              Entra nella sessione
            </button>
            <div className="text-[11px] text-muted-foreground">Entri senza account. Il nickname serve solo al docente.</div>
          </div>
        </div>
      </div>
    );
  }

  const sessionStatus = session?.status || 'lobby';

  return (
    <div className={"min-h-screen bg-background text-foreground flex items-center justify-center p-4 " + (isInclusive ? 'text-lg' : '')}>
      <div className="w-full max-w-xl rounded-2xl border border-border bg-card overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground">Quiz Live</div>
            <div className="text-sm font-semibold text-foreground truncate">Codice {code || '—'}</div>
          </div>
          <button
            type="button"
            onClick={() => navigate(appPath('/live/join'))}
            className="px-3 py-1.5 rounded-xl bg-muted text-foreground text-xs"
          >
            Esci
          </button>
        </div>

        <div className="p-4">
          {sessionStatus === 'lobby' ? (
            <div className="space-y-2">
              <div className="text-lg font-semibold">In attesa…</div>
              <div className="text-sm text-muted-foreground">Attendi la prossima domanda del docente.</div>
            </div>
          ) : null}

          {sessionStatus === 'ended' ? (
            <div className="space-y-4">
              <div>
                <div className="text-lg font-semibold">Sessione terminata</div>
                <div className="text-sm text-muted-foreground">Grazie per aver partecipato.</div>
              </div>

              {finalParticipants.length ? (
                <div>
                  <div className="text-sm font-semibold text-foreground">Classifica finale</div>
                  <div className="mt-2 space-y-1.5">
                    {finalParticipants.slice(0, 20).map((p: any, idx: number) => {
                      const isMe = participantId && p.id === participantId;
                      const isPodium = idx < 3;
                      const medals = ['🥇', '🥈', '🥉'];
                      return (
                        <div
                          key={p.id}
                          className={
                            'flex items-center justify-between gap-3 rounded-xl border px-3 py-2 ' +
                            (isMe
                              ? 'border-primary/50 bg-primary/10'
                              : isPodium
                              ? 'border-amber-500/30 bg-amber-500/5'
                              : 'border-border bg-card')
                          }
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-7 text-center text-base">
                              {isPodium ? medals[idx] : <span className="text-muted-foreground text-xs font-mono">#{idx + 1}</span>}
                            </div>
                            <div className="text-sm font-medium text-foreground truncate">
                              {String(p.nickname || 'Studente')}
                              {isMe ? <span className="ml-1.5 text-xs text-primary">(tu)</span> : null}
                            </div>
                          </div>
                          <div className="text-sm font-mono font-semibold text-foreground shrink-0">
                            {Number(p.score || 0)} pt
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {sessionStatus === 'question' && currentQuestion ? (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground">Domanda</div>
                  <div className={"font-semibold " + (isInclusive ? 'text-2xl' : 'text-lg')}>{currentQuestion.prompt}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Tempo</div>
                  <div className={"font-bold tabular-nums " + (msLeft(currentEndsAtMs) <= 0 ? 'text-muted-foreground' : 'text-foreground')}>
                    {Math.max(0, timeLeftSec)}s
                  </div>
                  {/* Visual timer bar */}
                  {currentEndsAtMs && currentDurationMs ? (
                    <div className="mt-2 w-28 sm:w-32">
                      <div className="h-2 rounded-full bg-muted/50 overflow-hidden border border-border">
                        <div
                          className={
                            'h-full rounded-full transition-[width] duration-500 ' +
                            (timeLeftMs <= 5000 && timeLeftMs > 0 ? 'bg-destructive' : 'bg-primary')
                          }
                          style={{ width: `${timerPct}%` }}
                        />
                      </div>
                      {currentStartedAtMs ? (
                        <div className="mt-1 text-[10px] text-muted-foreground">
                          {Math.round(currentDurationMs / 1000)}s totali
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => speak(currentQuestion.prompt + ' ' + (currentQuestion.options || []).map((o) => o.text).join('. '))}
                  className="px-3 py-2 rounded-xl bg-muted text-foreground text-sm"
                >
                  Leggi
                </button>
                {session?.current?.mode === 'collective' ? (
                  <div className="text-sm text-muted-foreground">Modalità collettiva: risponde il docente.</div>
                ) : null}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {(currentQuestion.options || []).map((o) => {
                  const isPicked = lastAnsweredQuestionId === currentQuestion.id && lastOptionId === o.id;
                  const isPending = pendingOptionId === o.id;
                  return (
                    <button
                      key={o.id}
                      type="button"
                      disabled={disableAnswering || submitting || hasAnsweredThis}
                      onClick={() => setPendingOptionId(o.id)}
                      className={
                        'w-full text-left px-3 py-3 rounded-2xl border transition ' +
                        (isPicked ? 'border-primary bg-muted' : isPending ? 'border-primary/60 bg-muted/60' : 'border-border hover:bg-muted/50') +
                        (disableAnswering || hasAnsweredThis ? ' opacity-70' : '')
                      }
                    >
                      <div className={isInclusive ? 'text-xl' : 'text-base'}>{o.text}</div>
                    </button>
                  );
                })}
              </div>

              {session?.current?.mode !== 'collective' ? (
                <button
                  type="button"
                  onClick={() => confirmPending()}
                  disabled={!canConfirm}
                  className={
                    'w-full px-4 py-3 rounded-2xl font-semibold transition ' +
                    (canConfirm ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')
                  }
                >
                  {submitting ? 'Invio…' : hasAnsweredThis ? 'Risposta inviata' : 'Conferma risposta'}
                </button>
              ) : null}

              {hasAnsweredThis ? (
                <div className="rounded-2xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Risposta inviata. Attendi la prossima domanda del docente.
                </div>
              ) : null}
              {!hasAnsweredThis && msLeft(currentEndsAtMs) <= 0 ? (
                <div className="rounded-2xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground">
                  Tempo scaduto. Attendi la prossima domanda del docente.
                </div>
              ) : null}
            </div>
          ) : null}

          {sessionStatus === 'results' && currentQuestion ? (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">Risultati</div>
              <div className={"font-semibold " + (isInclusive ? 'text-2xl' : 'text-lg')}>{currentQuestion.prompt}</div>

              <div className="rounded-2xl border border-border bg-background p-3">
                <div className="text-sm text-muted-foreground">Soluzione corretta</div>
                <div className="text-base font-semibold text-foreground">
                  {(currentQuestion.options || []).find((o) => o.id === (session as any)?.current?.results?.correctOptionId)?.text || '—'}
                </div>
              </div>

              {lastAnsweredQuestionId === currentQuestion.id ? (
                <div className="text-sm">
                  La tua risposta: <span className="font-medium">{(currentQuestion.options || []).find((o) => o.id === lastOptionId)?.text || '—'}</span>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">Non hai risposto a questa domanda.</div>
              )}

              {String((session as any)?.current?.results?.explanation || '').trim() ? (
                <div className="text-sm text-muted-foreground">{String((session as any)?.current?.results?.explanation || '')}</div>
              ) : null}
            </div>
          ) : null}

          <div className="hidden">{tick}</div>
        </div>
      </div>
    </div>
  );
}
